import ky, { HTTPError } from 'ky'
import { captureException } from '@sentry/browser'
import { UserProfile } from './types'

const options = {
  hooks: {
    afterResponse: [
      (_input, _options, response) => {
        if (response.status >= 500) {
          captureException(new HTTPError(response))
        }
      },
    ],
  },
  headers: { 'Content-Type': 'application/json' },
  credentials: 'same-origin',
}

export class ArcHomeError extends Error {
  static ERROR_UNKNOWN = 'UNKNOWN'

  constructor(data) {
    const { code = ArcHomeError.ERROR_UNKNOWN, context } = data || {}
    super(code)
    Object.assign(this, {
      code,
      context,
    })
  }
}

function createService(prefixUrl, opts = options) {
  return ky.create({
    prefixUrl,
    ...(opts as any),
  })
}

export const SERVICE = {
  siteRead: createService('/siteservice-readonly/api'),
  home: createService('/home/api'),
  settings: createService('/home/api/settings'),
  trident: createService('/'),
}

async function httpErrorToArcHomeError(error) {
  const { response } = error || {}
  if (!response) {
    throw error
  }
  if (!response.headers.has('content-type')) {
    throw error
  }
  if (!~response.headers.get('content-type').indexOf('json')) {
    throw error
  }
  const json = await response.json()
  throw new ArcHomeError(json)
}

/**
 * These functions should conform to the react-async's
 * [promiseFn or deferFn API]{@link https://github.com/async-library/react-async#promisefn}
 * so they can be passed directly to the `useAsync` hook.
 */
export const URL = {
  home: {
    getTrendingContent: ({ website, integrationType }, { signal }) => {
      if (website === undefined) {
        return Promise.resolve([])
      }
      return SERVICE.home
        .get(
          `v1/modules/trendingContent?website=${website}&integrationType=${integrationType}`,
          {
            signal,
          }
        )
        .json()
        .catch(httpErrorToArcHomeError)
    },
  },
  site: {
    getWebsites: (_, { signal }) =>
      SERVICE.siteRead
        .get(`v3/website`, { signal })
        .json()
        .catch(httpErrorToArcHomeError),
  },
  settings: {
    getIntegrations: (_, { signal }) =>
      SERVICE.settings
        .get(`v1/integration`, { signal })
        .json()
        .catch(httpErrorToArcHomeError),
    getUserExtra: (_, { signal }) =>
      SERVICE.settings
        .get('v1/user')
        .json()
        .catch(httpErrorToArcHomeError),
    saveUserExtra: ([extra], _, { signal }) =>
      SERVICE.settings
        .post('v1/user', { body: JSON.stringify(extra), signal })
        .json()
        .catch(httpErrorToArcHomeError),
    getUserProfile: (_, { signal }) =>
      SERVICE.settings
        .get('v1/user/profile', { signal })
        .json<UserProfile>()
        .catch(httpErrorToArcHomeError),
    saveUserProfile: (userProfile: UserProfile, { signal }) =>
      SERVICE.settings
        .put('v1/user/profile', { body: JSON.stringify(userProfile), signal })
        .json<UserProfile>()
        .catch(httpErrorToArcHomeError),
  },
  trident: {
    getPermissions: (_, { signal }) =>
      SERVICE.trident
        .get(`user.json`, { signal })
        .json()
        .catch(httpErrorToArcHomeError),
    setTracking: ([status], _, { signal }) =>
      SERVICE.trident
        .post('updateTrackingPrefs', { body: JSON.stringify(status), signal })
        .json()
        .catch(httpErrorToArcHomeError),
  },
}
